import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { rewardApi } from "../../api/rewardApi";
import logo from "../../assets/images/logo.png";
import "./reward-entry.scss";

const QR_REGION_ID = "reward-qr-reader";

const RewardEntryPage = () => {
    const scannerRef = useRef(null);
    const isHandlingScanRef = useRef(false);

    const [invoiceNo, setInvoiceNo] = useState("");
    const [target, setTarget] = useState("");
    const [phone, setPhone] = useState("");
    const [isScanned, setIsScanned] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [popup, setPopup] = useState({
        show: false,
        type: "",
        title: "",
        message: "",
    });

    useEffect(() => {
        startScanner();

        return () => {
            stopScanner();
        };
    }, []);

    const startScanner = async () => {
        if (scannerRef.current) {
            return;
        }

        isHandlingScanRef.current = false;

        const html5QrCode = new Html5Qrcode(QR_REGION_ID);
        scannerRef.current = html5QrCode;

        const config = {
            fps: 10,
            qrbox: {
                width: 260,
                height: 260,
            },
        };

        try {
            await html5QrCode.start(
                { facingMode: "environment" },
                config,
                handleQrSuccess,
                () => {
                    // Ignore scan failure while camera is active.
                }
            );
        } catch (err) {
            await startScannerWithFallbackCamera(html5QrCode, config);
        }
    };

    const startScannerWithFallbackCamera = async (html5QrCode, config) => {
        try {
            const cameras = await Html5Qrcode.getCameras();

            if (!cameras.length) {
                throw new Error("No camera found.");
            }

            const backCamera = cameras.find((camera) => {
                return /back|rear|environment/i.test(camera.label);
            });

            await html5QrCode.start(
                backCamera ? backCamera.id : cameras[0].id,
                config,
                handleQrSuccess,
                () => {
                    // Ignore scan failure while camera is active.
                }
            );
        } catch (err) {
            scannerRef.current = null;

            showPopup({
                type: "error",
                title: "Camera Error",
                message: "Unable to start the camera. Please allow camera permission and try again.",
            });
        }
    };

    const stopScanner = async () => {
        if (!scannerRef.current) {
            return;
        }

        try {
            const state = scannerRef.current.getState?.();

            if (state === 2) {
                await scannerRef.current.stop();
            }

            await scannerRef.current.clear();
        } catch (err) {
            // Ignore scanner cleanup error.
        }

        scannerRef.current = null;
    };

    const extractReceiptData = (text) => {
        try {
            const url = new URL(text);
            const parts = url.pathname.split("/").filter(Boolean);
            const invoiceIndex = parts.indexOf("einvoice");

            if (invoiceIndex === -1 || !parts[invoiceIndex + 1]) {
                return {
                    invoice_no: "",
                    target: "",
                };
            }

            return {
                invoice_no: parts[invoiceIndex + 1],
                target: url.searchParams.get("target") || "",
            };
        } catch (err) {
            return {
                invoice_no: "",
                target: "",
            };
        }
    };

    const handleQrSuccess = async (decodedText) => {
        if (isHandlingScanRef.current) {
            return;
        }

        isHandlingScanRef.current = true;

        const result = extractReceiptData(decodedText);

        if (!result.invoice_no) {
            isHandlingScanRef.current = false;

            showPopup({
                type: "error",
                title: "Invalid QR Code",
                message: "Please scan a valid receipt QR code.",
            });
            return;
        }

        setInvoiceNo(result.invoice_no);
        setTarget(result.target || "");
        setIsScanned(true);

        await stopScanner();
    };

    const showPopup = ({ type, title, message }) => {
        setPopup({
            show: true,
            type,
            title,
            message,
        });
    };

    const closePopup = () => {
        setPopup({
            show: false,
            type: "",
            title: "",
            message: "",
        });
    };

    const resetScanner = () => {
        setInvoiceNo("");
        setTarget("");
        setPhone("");
        setIsScanned(false);
        closePopup();

        setTimeout(() => {
            startScanner();
        }, 300);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!phone.trim()) {
            showPopup({
                type: "error",
                title: "Phone Number Required",
                message: "Please enter your phone number.",
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const data = await rewardApi.claimReceipt({
                invoice_no: invoiceNo,
                phone: phone.trim(),
                target,
            });

            showPopup({
                type: "success",
                title: "Claim Submitted",
                message: data.message || "Your receipt claim has been submitted successfully.",
            });
        } catch (err) {
            showPopup({
                type: "error",
                title: "Claim Failed",
                message: err.message || "Unable to submit this receipt claim.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="reward-entry">
            <main className="reward-entry__card">
                <img src={logo} alt="Logo" className="reward-entry__logo" />

                <h1>Scan Receipt</h1>

                <p className="reward-entry__subtitle">
                    Scan your receipt QR code to claim your points.
                </p>

                {!isScanned && (
                    <div className="reward-entry__scanner">
                        <div id={QR_REGION_ID} />
                    </div>
                )}

                {isScanned && (
                    <form className="reward-entry__form" onSubmit={handleSubmit}>
                        <div className="reward-entry__summary">
                            <div>
                                <span>Invoice No.</span>
                                <strong>{invoiceNo}</strong>
                            </div>

                            {target && (
                                <div>
                                    <span>Target</span>
                                    <strong>{target}</strong>
                                </div>
                            )}
                        </div>

                        <div className="reward-entry__field">
                            <label>Phone Number</label>
                            <input
                                type="tel"
                                value={phone}
                                placeholder="Example: 0123456789"
                                onChange={(e) => setPhone(e.target.value)}
                            />
                        </div>

                        <button
                            type="submit"
                            className="reward-entry__button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Submitting..." : "Submit Claim"}
                        </button>

                        <button
                            type="button"
                            className="reward-entry__link-button"
                            onClick={resetScanner}
                        >
                            Scan Again
                        </button>
                    </form>
                )}
            </main>

            {popup.show && (
                <div className="reward-entry__popup">
                    <div className="reward-entry__popup-card">
                        <div
                            className={`reward-entry__popup-icon reward-entry__popup-icon--${popup.type}`}
                        >
                            {popup.type === "success" ? "✓" : "!"}
                        </div>

                        <h2>{popup.title}</h2>
                        <p>{popup.message}</p>

                        <button
                            type="button"
                            onClick={popup.type === "success" ? resetScanner : closePopup}
                        >
                            {popup.type === "success" ? "Done" : "Close"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RewardEntryPage;
